import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Row, Col, Card, Form, Table, Spinner,
} from 'react-bootstrap';
import { toast } from "react-hot-toast";
import {
  FaClipboardList,
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { FiHome, FiChevronRight } from "react-icons/fi";
import Select from "react-select";

import { getClassteacher } from '../../store/slices/classteacher';
import { getStudent } from '../../store/slices/student';
import { getsetMarks } from '../../store/slices/setmarks';
import { getExam } from '../../store/slices/exam';
import { addResult } from '../../store/slices/result';


const getCurrentSession = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0 = Jan, 3 = Apr

  // Academic year starts in April
  const startYear = month >= 3 ? year : year - 1;

  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const buildSessionOptions = () => {
  return [getCurrentSession()];
};

export default function TeacherReportCard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { exam = [] } = useSelector((state) => state.exam);

  const [teacherId, setTeacherId] = useState('');

  const [standardId, setStandardId] = useState('');
  const [standardName, setStandardName] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [sectionName, setSectionName] = useState('');

  const [session, setSession] = useState(getCurrentSession());
  const [examId, setExamId] = useState('');

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');

  const [subjects, setSubjects] = useState([]);
  const [marks, setMarks] = useState({});

  const [loadingClass, setLoadingClass] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMarksTable, setShowMarksTable] = useState(false);


  useEffect(() => {
    const init = async () => {
      try {
        const userStr = localStorage.getItem('user')

        const userObj = JSON.parse(userStr);
        const tId = userObj?.data?.teacherId || userObj?.teacherId;


        setTeacherId(tId);

        setLoadingClass(true);
        const res = await dispatch(getClassteacher({ teacherId: tId })).unwrap();

        const classInfo = Array.isArray(res?.data) ? res.data[0] : res?.data;

        if (classInfo) {
          const stdId = classInfo.standardId?._id || classInfo.standardId || '';
          const stdName = classInfo.standardId?.name || classInfo.standardName || '';
          const secId = classInfo.sectionId?._id || classInfo.sectionId || '';
          const secName = classInfo.sectionId?.name || classInfo.sectionName || '';

          setStandardId(stdId);
          setStandardName(stdName);
          setSectionId(secId);
          setSectionName(secName);

          if (stdId && secId) {
            setLoadingStudents(true);
            const studentRes = await dispatch(getStudent({ standardId: stdId, sectionId: secId })).unwrap();
            setStudents(studentRes?.data || []);
            setLoadingStudents(false);
          }
        } else {
          toast.error('No class assigned to this teacher.');
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to load class/section details');
      } finally {
        setLoadingClass(false);
      }
    };

    init();

  }, []);
  const studentOptions = students.map((stu) => ({
    value: stu._id,
    label: stu.name,
  }));

  useEffect(() => {
    const fetchExams = async () => {
      try {
        await dispatch(getExam()).unwrap();
      } catch (err) {
        toast.error(err?.message || 'Failed to load exams');
      }
    };
    fetchExams();

  }, []);


  const handleSubmit = async () => {

    try {
      setLoadingSubjects(true);

      const setMarksRes = await dispatch(getsetMarks({ standardId, examId })).unwrap();
      const cfg = Array.isArray(setMarksRes?.data) ? setMarksRes.data[0] : setMarksRes?.data;
      const subjectList = cfg?.subjects || [];

      if (subjectList.length === 0) {
        toast.error('No subjects configured for this exam & standard.');
        return;
      }

      setSubjects(subjectList);

      const initialMarks = {};
      subjectList.forEach((sub) => {
        initialMarks[sub.subjectId._id] = '';
      });
      setMarks(initialMarks);

      setShowMarksTable(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to fetch subjects');
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleMarkChange = (subjectId, value, maxMarks) => {

    // Allow empty while deleting
    if (value === "") {
      setMarks(prev => ({
        ...prev,
        [subjectId]: ""
      }));
      return;
    }

    // Allow typing "-" temporarily
    if (value === "-") {
      setMarks(prev => ({
        ...prev,
        [subjectId]: "-"
      }));
      return;
    }

    // Allow only integers with optional leading '-'
    if (!/^-?\d+$/.test(value)) {
      return;
    }

    const marks = Number(value);

    // Only -1 is allowed as a negative value
    if (marks < -1) {
      toast.error("Only -1 is allowed for Absent");
      return;
    }

    if (marks > Number(maxMarks)) {
      toast.error(`Marks cannot exceed ${maxMarks}`);
      return;
    }

    setMarks(prev => ({
      ...prev,
      [subjectId]: value
    }));

  };

  const validateMarks = () => {
    for (const sub of subjects) {
      const val = marks[sub.subjectId._id];
      if (val === '' || val === undefined || val === null) {
        toast.error(`Please enter ${sub.subjectId.name} marks`);
        return false;
      }
      if (Number(val) > sub.maxMarks) {
        toast.error(`${sub.subjectId.name} marks exceed max (${sub.maxMarks})`);
        return false;
      }

    }
    return true;
  };


  const handleSubmitMarks = async () => {
    if (!validateMarks()) return;

    try {
      setSubmitting(true);

      const payload = {
        session,
        standardId,
        sectionId,
        examId,
        studentId,
        subjects: subjects.map((sub) => ({
          subjectId: sub.subjectId._id,
          obtainedMarks: Number(marks[sub.subjectId._id]),
        })),
      };

      await dispatch(addResult(payload)).unwrap();

      toast.success("Marks saved successfully!");

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.log("Error:", err);

      toast.error(err || "Failed to submit marks");
    } finally {
      setSubmitting(false);
    }
  };



  const examOptions = exam?.data || exam || [];

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/teacher/home">
                <FiHome style={{ color: "white", marginTop: "-4px" }} />
              </Link>
            </div>

            <FiChevronRight className="separator" />
            <span className="active">Reportcard</span>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
          </div>

        </div>
      </div>
      <div className="trc-wrapper">
        <Container fluid className="py-4">
          <Card className="trc-filter-card mb-4 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={2} xs={12}>
                  <Form.Label className="trc-label">Standard</Form.Label>
                  <Form.Select className="trc-select" value={standardId} disabled>
                    <option value="">{loadingClass ? 'Loading...' : 'Not Assigned'}</option>
                    {standardId && <option value={standardId}>{standardName}</option>}
                  </Form.Select>
                </Col>

                <Col md={2} xs={12}>
                  <Form.Label className="trc-label">Section</Form.Label>
                  <Form.Select className="trc-select" value={sectionId} disabled>
                    <option value="">{loadingClass ? 'Loading...' : 'Not Assigned'}</option>
                    {sectionId && <option value={sectionId}>{sectionName}</option>}
                  </Form.Select>
                </Col>

                <Col md={2} xs={12}>
                  <Form.Label className="trc-label">Session</Form.Label>
                  <Form.Select
                    className="trc-select"
                    value={session}
                    onChange={(e) => {
                      setSession(e.target.value);

                      setShowMarksTable(false);
                      setSubjects([]);
                      setMarks({});
                    }}
                  >
                    {buildSessionOptions().map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3} xs={12}>
                  <Form.Label className="trc-label">Exam</Form.Label>
                  <Form.Select
                    className="trc-select"
                    value={examId}
                    onChange={(e) => {
                      setExamId(e.target.value);

                      setShowMarksTable(false);
                      setSubjects([]);
                      setMarks({});
                    }}
                  >
                    <option value="">Select Exam</option>
                    {examOptions.map((ex) => (
                      <option key={ex._id} value={ex._id}>
                        {ex.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3} xs={12}>
                  <Form.Label className="trc-label">Student</Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    isLoading={loadingStudents}
                    placeholder="Select Student"
                    options={studentOptions}
                    value={studentOptions.find((opt) => opt.value === studentId) || null}
                    onChange={(selected) => {
                      setStudentId(selected ? selected.value : "");
                      setShowMarksTable(false);
                      setSubjects([]);
                      setMarks({});
                    }}
                    isClearable
                    isSearchable
                  />
                </Col>

                <Col xs={12} className="text-center mt-5">
                  <span

                    onClick={handleSubmit}
                    disabled={loadingSubjects}
                  >
                    {loadingSubjects ? (
                      <>
                        <button type="submit" className="btn-submit">
                          <Spinner size="sm" animation="border" className="me-2" />
                          Loading...
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="submit" className="btn-submit">
                          Submit
                        </button>
                      </>
                    )}
                  </span>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {showMarksTable && (
            <Card className="trc-table-card border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="trc-table-title mb-0 d-flex align-items-center gap-2">
                    <FaClipboardList /> Enter Marks
                  </h6>

                </div>

                <div className="table-responsive">
                  <Table className="trc-table align-middle" bordered hover>
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Max Marks</th>
                        <th>Pass Marks</th>
                        <th>Obtained Marks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map((sub) => (
                        <tr key={sub._id}>
                          <td>{sub.subjectId.name}</td>
                          <td>{sub.maxMarks}</td>
                          <td>{sub.passMarks}</td>
                          <td>
                            <Form.Control
                              type="text"
                              className="trc-mark-input"
                              value={marks[sub.subjectId._id] ?? ""}
                              maxLength={4}
                              onChange={(e) =>
                                handleMarkChange(
                                  sub.subjectId._id,
                                  e.target.value,
                                  sub.maxMarks
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="text-center mt-3">
                  <span
                    onClick={handleSubmitMarks}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <button type="submit" className="btn-submit">
                          <Spinner size="sm" animation="border" className="me-2" />
                          Submitting...
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="submit" className="btn-submit">
                          Submit
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </Card.Body>
            </Card>
          )}
        </Container>

        <style>{` 
        .trc-filter-card,
        .trc-table-card {
          border-radius: 14px;
        }
        .trc-label {
          font-weight: 600;
          font-size: 0.85rem;
          color: #5e5e66;
          margin-bottom: 6px;
        }
        .trc-select,
        .trc-mark-input {
          border-radius: 8px;
          border: 1px solid #d8d3fb;
        }
        .trc-select:focus,
        .trc-mark-input:focus {
          border-color: #7c4dff;
          box-shadow: 0 0 0 0.2rem rgba(124, 77, 255, 0.15);
        }
        .trc-table-title {
          color: #4b3fbd;   
          font-weight: 700;
        }
        .trc-mark-input {
          max-width: 100px;
        }
        @media (max-width: 768px) {
          .trc-table {
            font-size: 0.85rem;
          }
          .trc-mark-input {
            max-width: 70px;
          }
        }
      `}</style>
      </div>
    </>
  );
}